import { useState } from 'react'
import React from 'react'

import {
  Box,
  Icon,
  Text,
  Touchable,
  useConfirmationDialog,
} from '@factorialco/deprecated-design-system'
import {
  AtsDeleteApplicationPhaseDocument,
  AtsHiringPipelinePhaseFragment,
  AtsReorderApplicationPhasesDocument,
} from 'generated/resources/graphql'
import { useDebounceCallback } from 'usehooks-ts'

import SortableBoxList from 'modules/core/components/SortableBoxList'
import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { HiringStageBox } from 'modules/ats/components/HiringStages/HiringStageBox'
import { HiringStageWithApplicationPhases } from 'modules/ats/pages/JobPostingSettings/HiringPipeline/utils'

import { AddApplicationPhaseModal } from './AddApplicationPhaseModal'
import { ApplicationPhaseListItem } from './ApplicationPhaseListItem'
import { DeleteApplicationPhaseModal } from './DeleteApplicationPhaseModal'

interface Props {
  jobId: number
  stage: HiringStageWithApplicationPhases
}

export const EditableHiringStage = ({ jobId, stage }: Props) => {
  const [addPhaseModalOpen, setAddPhaseModalOpen] = useState(false)
  const [deletedPhaseWithApplications, setDeletedPhaseWithApplications] =
    React.useState<AtsHiringPipelinePhaseFragment | null>(null)

  const [reorderApplicationPhases] = useMutation(AtsReorderApplicationPhasesDocument, {
    extractorFn: (data) => data.atsMutations.updatePositionsAtsApplicationPhase,
  })

  const { openDialog } = useConfirmationDialog()

  const onSortPhases = useDebounceCallback(
    async (reorderedItems: AtsHiringPipelinePhaseFragment[]) => {
      const newPositions = reorderedItems.map((phase, newPosition) => ({
        atsApplicationPhaseId: phase.id,
        newPosition,
      }))
      const result = await reorderApplicationPhases({ variables: { newPositions } })

      const handled = handleResult(result, () => undefined)

      if (handled.success) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('common.saved_successfully'),
          time: 2000,
        })
      }

      return handled
    },
    500
  )

  const [deleteAtsApplicationPhase] = useMutation(AtsDeleteApplicationPhaseDocument, {
    extractorFn: (data) => data.atsMutations.deleteAtsApplicationPhase,
  })

  const onDelete = async (applicationPhase: AtsHiringPipelinePhaseFragment) => {
    const result = await deleteAtsApplicationPhase({
      variables: {
        id: applicationPhase.id,
      },
      update(cache) {
        cache.evict({ id: cache.identify(applicationPhase) })
      },
    })

    const handled = handleResult(result, () => undefined)

    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('common.removed_successfully'),
      })
    }

    return handled
  }

  return (
    <HiringStageBox stageName={stage.name} stageLabel={stage.label}>
      <SortableBoxList
        items={stage.applicationPhases}
        emptyMessage={i18n.t('ats.job_posting_settings.hiring_pipeline.empty_hiring_phases')}
        onSortEnd={onSortPhases}
        itemAsKey={(applicationPhase) => applicationPhase.id.toString()}
        renderItem={(applicationPhase) => (
          <ApplicationPhaseListItem
            applicationPhase={applicationPhase}
            onDelete={() =>
              !applicationPhase.activeApplicationsCount
                ? openDialog({
                    title: i18n.t(
                      'ats.job_posting_settings.hiring_pipeline.delete_phase_modal.title',
                      {
                        phaseName: applicationPhase.name,
                      }
                    ),
                    illustration: 'delete',
                    content: i18n.t(
                      'ats.job_posting_settings.hiring_pipeline.delete_phase_modal.description'
                    ),
                    cancelLabel: i18n.t('common.cancel'),
                    confirmLabel: i18n.t('common.delete'),
                    onConfirm: () => onDelete(applicationPhase),
                  })
                : setDeletedPhaseWithApplications(applicationPhase)
            }
          />
        )}
      />

      <Touchable onClick={() => setAddPhaseModalOpen(true)}>
        <Box flexDirection="row" alignItems="center">
          <Icon.Small icon="add" color="viridian1200" />
          <Text.Normal color="viridian1200" size="200" weight="medium">
            {i18n.t('ats.job_posting_settings.hiring_pipeline.new_phase')}
          </Text.Normal>
        </Box>
      </Touchable>

      {addPhaseModalOpen && (
        <AddApplicationPhaseModal
          jobId={jobId}
          hiringStage={stage}
          onClose={() => setAddPhaseModalOpen(false)}
        />
      )}

      {deletedPhaseWithApplications && (
        <DeleteApplicationPhaseModal
          id={deletedPhaseWithApplications.id}
          jobId={jobId}
          onClose={() => setDeletedPhaseWithApplications(null)}
        />
      )}
    </HiringStageBox>
  )
}
